import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Icon, type TabItem } from '@uplink/ui';
import { errorText, useTRPC } from '../api.ts';
import logo from '../assets/uplink-logo.svg';
import type { Nav, Tab } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import { openPayUrl, telegramMiniAppUrl } from '../telegram.ts';

/** The website's navigation on a wide screen: the brand, the sections, the balance and who is signed in. */
export function WebSidebar({ tabs, active, nav }: { tabs: TabItem<Tab>[]; active: Tab; nav: Nav }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { t, money } = usePrefs();
  const me = useQuery(trpc.me.queryOptions());
  const balance = useQuery(trpc.balance.summary.queryOptions());
  const config = useQuery(trpc.config.queryOptions());
  const linkTelegram = useMutation(
    trpc.auth.linkTelegram.mutationOptions({
      onSuccess: (result) => openPayUrl(result.link),
    }),
  );
  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: () => {
        queryClient.clear();
        window.location.assign('/');
      },
    }),
  );
  const devAdminLogin = useMutation(
    trpc.auth.devLogin.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(),
    }),
  );

  return (
    <aside className="ul-sidebar">
      <a className="ul-sidebar__brand" href="/">
        <img src={logo} alt="" width={32} height={32} />
        Uplink
      </a>

      <nav className="ul-sidebar__nav" aria-label={t.web.sections}>
        {tabs.map((it) => {
          const current = it.id === active;
          return (
            <button
              key={it.id}
              type="button"
              className="ul-sidebar__item"
              aria-current={current ? 'page' : undefined}
              onClick={() => nav.tab(it.id)}
            >
              <Icon name={it.icon} size={19} weight={current ? 'fill' : 'regular'} />
              {it.label}
            </button>
          );
        })}
        {me.data?.isAdmin ? (
          <button
            type="button"
            className="ul-sidebar__item ul-sidebar__item--admin"
            onClick={() => nav.push({ name: 'admin' })}
          >
            <Icon name="chart" size={19} />
            Админ-панель
          </button>
        ) : import.meta.env.DEV ? (
          <button
            type="button"
            className="ul-sidebar__item ul-sidebar__item--admin"
            disabled={devAdminLogin.isPending}
            onClick={() => devAdminLogin.mutate({})}
          >
            <Icon name="chart" size={19} />
            {devAdminLogin.isPending ? 'Входим…' : 'Войти администратором'}
          </button>
        ) : null}
      </nav>

      <div className="ul-sidebar__foot">
        {config.data?.botUrl && me.data ? (
          <button
            type="button"
            className="ul-sidebar__telegram"
            disabled={linkTelegram.isPending}
            onClick={() => {
              if (me.data.telegramId) openPayUrl(telegramMiniAppUrl(config.data!.botUrl!));
              else linkTelegram.mutate();
            }}
          >
            <Icon name="bolt" size={19} weight="fill" />
            <span>
              <b>{me.data.telegramId ? t.web.openTelegram : t.web.linkTelegram}</b>
              <small>
                {linkTelegram.isError
                  ? errorText(linkTelegram.error)
                  : linkTelegram.isPending
                    ? t.web.linkingTelegram
                    : me.data.telegramId
                      ? t.web.telegramLinked(me.data.username)
                      : t.web.telegramNotLinked}
              </small>
            </span>
          </button>
        ) : null}
        {balance.data ? (
          <div className="ul-sidebar__balance">
            <span>{t.desk.balance}</span>
            <b>{money(balance.data.balanceCents)}</b>
            <button type="button" onClick={() => nav.push({ name: 'topup' })}>
              <Icon name="plus" size={16} />
              {t.desk.topUp}
            </button>
          </div>
        ) : null}
        {me.data ? (
          <div className="ul-sidebar__account">
            <button type="button" className="ul-sidebar__who" onClick={() => nav.tab('profile')}>
              <Avatar src={me.data.photoUrl} name={me.data.firstName} size={34} />
              <span>
                <b>{me.data.firstName}</b>
                <small>
                  {me.data.email ??
                    (me.data.telegramId ? t.web.telegramLinked(me.data.username) : t.web.telegramNotLinked)}
                </small>
              </span>
            </button>
            {me.data.web ? (
              <button
                type="button"
                className="ul-sidebar__out"
                aria-label={t.desk.signOut}
                title={t.desk.signOut}
                disabled={logout.isPending}
                onClick={() => logout.mutate()}
              >
                <Icon name="signOut" size={18} />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </aside>
  );
}
